import React, { useState, useEffect, useCallback, useMemo } from 'react';
import supabase from '../supabaseClient';
import { todayLocal, formatDate as formatDateLocaleAware } from '../utils/dateLocal';
import { onEnterOrSpace, useEscapeToClose } from '../utils/a11y';
import { confirmDialog } from './ConfirmDialog';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Only tennis, squash and badminton are played with a strung racket —
// padel/pickleball bats and table tennis blades are solid, so the whole
// stringing workflow (Restring button, gauge/tension fields, stringing
// history) only makes sense for those three. See user request 2026-10-24.
const SPORTS_WITH_STRINGS = ['tennis', 'squash', 'badminton'];
export function sportHasStrings(sport) { return SPORTS_WITH_STRINGS.includes(sport); }

// What to call the "racket" item per sport — same underlying
// equipment_items.type ('racket'), just a different word in the UI so a
// padel or table tennis player isn't told to "restring their racket".
const RACKET_WORD = {
  padel: 'Paddle', pickleball: 'Paddle', table_tennis: 'Blade',
};
export function racketLabel(sport) { return RACKET_WORD[sport] || 'Racket'; }

// ── Design (same tokens as GamePlanTab.jsx / MatchesCompetitionsReport.jsx) ──
const C = {
  bg:      '#0d1117',
  card:    'rgba(13,17,23,0.92)',
  // Brightened per user feedback — see GamePlanTab.jsx's C.border/C.muted.
  border:  'rgba(255,255,255,0.22)',
  accent:  '#10b981',
  blue:    '#3b82f6',
  // ZenAnalytics wordmark blue — module card borders, see GamePlanTab.jsx.
  logoBlue: '#38bdf8',
  moduleBorder: 'rgba(56,189,248,0.65)',
  amber:   '#f59e0b',
  red:     '#ef4444',
  purple:  '#a78bfa',
  text:    '#e6edf3',
  muted:   '#a8b3bd',
  dim:     'rgba(255,255,255,0.45)',
  font:    "'Inter', system-ui, sans-serif",
};

/**
 * Resolve the equipment a Game Plan should be snapshotted with at save
 * time: the user's default racket + shoes, and whichever stringing is
 * currently active (archived_at IS NULL) on that racket. Called once
 * from SessionModal.handleSave — the result is written directly onto
 * the session_plans row and never moves again, even if the racket gets
 * restrung or the defaults change later. That's what keeps old matches
 * attributed to the gear they were actually played with.
 */
export async function getDefaultEquipmentSnapshot(userId, sport) {
  const empty = { equipment_racket_id: null, equipment_stringing_id: null, equipment_shoes_id: null };
  if (!userId || !sport) return empty;
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('default_equipment')
      .eq('user_id', userId)
      .maybeSingle();
    const forSport = profile?.default_equipment?.[sport] || {};
    const racketId = forSport.racket_id || null;
    const shoesId = forSport.shoes_id || null;
    let stringingId = null;
    if (racketId) {
      const { data: str } = await supabase
        .from('equipment_stringings')
        .select('id')
        .eq('racket_id', racketId)
        .is('archived_at', null)
        .order('strung_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      stringingId = str?.id || null;
    }
    return { equipment_racket_id: racketId, equipment_stringing_id: stringingId, equipment_shoes_id: shoesId };
  } catch {
    return empty;
  }
}

const inp = {
  width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.09)',
  border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px',
  color: C.text, fontSize: 13, fontFamily: C.font, outline: 'none',
};
const lbl = { display: 'block', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };

function fmtStringing(s) {
  if (!s) return null;
  const parts = [];
  if (s.string_brand || s.string_model) parts.push([s.string_brand, s.string_model].filter(Boolean).join(' '));
  if (s.gauge_mm) parts.push(`${s.gauge_mm}mm`);
  if (s.main_tension_kg || s.cross_tension_kg) {
    parts.push(s.main_tension_kg && s.cross_tension_kg && s.main_tension_kg !== s.cross_tension_kg
      ? `${s.main_tension_kg}/${s.cross_tension_kg}kg`
      : `${s.main_tension_kg || s.cross_tension_kg}kg`);
  }
  return parts.join(' · ') || null;
}

// ── Racket/shoes item form ──────────────────────────────────────────────
function ItemFormModal({ item, type, sport, userId, onSaved, onClose }) {
  const isNew = !item?.id;
  const itemWord = type === 'racket' ? racketLabel(item?.sport || sport) : 'shoes';
  const [brand, setBrand] = useState(item?.brand || '');
  const [model, setModel] = useState(item?.model || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!brand.trim() || !model.trim()) { setError('Brand and model are required.'); return; }
    // Same sport=null race as Game Plan/Competition forms: a new item
    // saved before the profile's active sport has loaded gets sport=null
    // and silently drops out of every sport-filtered equipment view.
    if (isNew && !item?.sport && !sport) {
      setError('Still loading your sport — wait a moment and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    // sport is fixed at creation (whichever sport you're viewing) and never
    // changes on edit — an item is scoped to one sport for its whole life.
    const payload = isNew
      ? { user_id: userId, type, sport, brand: brand.trim(), model: model.trim() }
      : { brand: brand.trim(), model: model.trim() };
    try {
      const { error: dbErr } = isNew
        ? await supabase.from('equipment_items').insert(payload)
        : await supabase.from('equipment_items').update(payload).eq('id', item.id);
      if (dbErr) throw dbErr;
      onSaved();
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  useEscapeToClose(onClose);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2200, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)',
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>
            {isNew ? `New ${itemWord}` : `Edit ${itemWord}`}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', color: '#fca5a5', fontSize: 12.5, marginBottom: 14 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <span style={lbl}>Brand</span>
            <input value={brand} onChange={e => setBrand(e.target.value)} placeholder={type === 'racket' ? 'e.g. Technifibre' : 'e.g. Puma'} style={inp} autoFocus />
          </div>
          <div>
            <span style={lbl}>Model</span>
            <input value={model} onChange={e => setModel(e.target.value)} placeholder={type === 'racket' ? 'e.g. TF40' : 'e.g. Aero 2.0'} style={inp} />
          </div>
          <button onClick={handleSave} disabled={saving} style={{
            background: C.accent, border: 'none', borderRadius: 8, padding: '11px 0',
            color: '#04150f', fontWeight: 700, fontSize: 13.5, cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1, fontFamily: C.font,
          }}>{saving ? 'Saving…' : (isNew ? 'Add' : 'Save changes')}</button>
        </div>
      </div>
    </div>
  );
}

// ── Restring form (new stringing on a racket) ───────────────────────────
function StringingFormModal({ racket, current, userId, onSaved, onClose }) {
  const [stringBrand, setStringBrand] = useState(current?.string_brand || '');
  const [stringModel, setStringModel] = useState(current?.string_model || '');
  const [gauge, setGauge] = useState(current?.gauge_mm ?? '');
  const [mainTension, setMainTension] = useState(current?.main_tension_kg ?? '');
  const [crossTension, setCrossTension] = useState(current?.cross_tension_kg ?? '');
  const [strungDate, setStrungDate] = useState(todayLocal());
  // Retrospective note on the setup being retired — repeat it, what to
  // change next time and why. Saved onto the archived row, not the new
  // one, since it's a verdict on how that setup performed. See user
  // request 2026-10-24: also feeds Data Coach's context.
  const [retiringNotes, setRetiringNotes] = useState(current?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Archive the racket's current stringing (if any) before inserting
      // the new one, so "active stringing" (archived_at IS NULL) always
      // resolves to exactly one row per racket.
      if (current?.id) {
        const { error: archErr } = await supabase
          .from('equipment_stringings')
          .update({ archived_at: new Date().toISOString(), notes: retiringNotes.trim() || null })
          .eq('id', current.id);
        if (archErr) throw archErr;
      }
      const { error: insErr } = await supabase.from('equipment_stringings').insert({
        user_id: userId,
        racket_id: racket.id,
        string_brand: stringBrand.trim() || null,
        string_model: stringModel.trim() || null,
        gauge_mm: gauge === '' ? null : parseFloat(gauge),
        main_tension_kg: mainTension === '' ? null : parseFloat(mainTension),
        cross_tension_kg: crossTension === '' ? null : parseFloat(crossTension),
        strung_date: strungDate,
      });
      if (insErr) throw insErr;
      onSaved();
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  useEscapeToClose(onClose);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2200, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)',
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
        maxHeight: '85vh', overflowY: 'auto', fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>Restring {racket.brand} {racket.model}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
          The current stringing gets archived, not deleted — matches already logged with it keep showing the old string setup.
        </div>
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', color: '#fca5a5', fontSize: 12.5, marginBottom: 14 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {current?.id && (
            <div>
              <span style={lbl}>Notes on the setup you're retiring</span>
              <textarea
                value={retiringNotes} onChange={e => setRetiringNotes(e.target.value)}
                placeholder="How did it perform? Repeat it, or what are you changing next time and why?"
                rows={3} style={{ ...inp, resize: 'vertical', fontFamily: C.font }}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={lbl}>String brand</span>
              <input value={stringBrand} onChange={e => setStringBrand(e.target.value)} placeholder="e.g. Technifibre" style={inp} autoFocus />
            </div>
            <div style={{ flex: 1 }}>
              <span style={lbl}>String model</span>
              <input value={stringModel} onChange={e => setStringModel(e.target.value)} placeholder="e.g. Black Code" style={inp} />
            </div>
          </div>
          <div>
            <span style={lbl}>Gauge (mm)</span>
            <input type="number" step="0.01" value={gauge} onChange={e => setGauge(e.target.value)} placeholder="e.g. 1.28" style={inp} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={lbl}>Main tension (kg)</span>
              <input type="number" step="0.5" value={mainTension} onChange={e => setMainTension(e.target.value)} placeholder="e.g. 21" style={inp} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={lbl}>Cross tension (kg)</span>
              <input type="number" step="0.5" value={crossTension} onChange={e => setCrossTension(e.target.value)} placeholder="e.g. 20" style={inp} />
            </div>
          </div>
          <div>
            <span style={lbl}>Strung on</span>
            <input type="date" value={strungDate} onChange={e => setStrungDate(e.target.value)} style={inp} />
          </div>
          <button onClick={handleSave} disabled={saving} style={{
            background: C.accent, border: 'none', borderRadius: 8, padding: '11px 0',
            color: '#04150f', fontWeight: 700, fontSize: 13.5, cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1, fontFamily: C.font,
          }}>{saving ? 'Saving…' : 'Save stringing'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Edit/delete an existing stringing entry ─────────────────────────────
// A bare "Delete" button on every history row (per racket, per stringing)
// was blunt -- it could only remove, never fix a typo in gauge/tension/date.
// This opens from that same row and does both: edit it in place, or delete
// it, from one modal. Feedback 2026-11-02.
function EditStringingModal({ stringing, racket, onSaved, onDeleted, onClose }) {
  const [stringBrand, setStringBrand] = useState(stringing.string_brand || '');
  const [stringModel, setStringModel] = useState(stringing.string_model || '');
  const [gauge, setGauge] = useState(stringing.gauge_mm ?? '');
  const [mainTension, setMainTension] = useState(stringing.main_tension_kg ?? '');
  const [crossTension, setCrossTension] = useState(stringing.cross_tension_kg ?? '');
  const [strungDate, setStrungDate] = useState(stringing.strung_date?.slice(0, 10) || '');
  const [notes, setNotes] = useState(stringing.notes || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const { error: updErr } = await supabase.from('equipment_stringings').update({
        string_brand: stringBrand.trim() || null,
        string_model: stringModel.trim() || null,
        gauge_mm: gauge === '' ? null : parseFloat(gauge),
        main_tension_kg: mainTension === '' ? null : parseFloat(mainTension),
        cross_tension_kg: crossTension === '' ? null : parseFloat(crossTension),
        strung_date: strungDate || null,
        notes: notes.trim() || null,
      }).eq('id', stringing.id);
      if (updErr) throw updErr;
      onSaved();
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const label = fmtStringing(stringing) || 'this stringing';
    const ok = await confirmDialog(
      `Delete ${label}? Any matches logged against it will be unlinked, not deleted.`,
      { title: 'Delete stringing entry', confirmLabel: 'Delete', danger: true }
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      const { error: unlinkErr } = await supabase.from('session_plans').update({ equipment_stringing_id: null }).eq('equipment_stringing_id', stringing.id);
      if (unlinkErr) throw unlinkErr;
      const { error: delErr } = await supabase.from('equipment_stringings').delete().eq('id', stringing.id);
      if (delErr) throw delErr;
      onDeleted();
    } catch (e) {
      setError(e?.message || 'Delete failed.');
      setDeleting(false);
    }
  };

  useEscapeToClose(onClose);
  const busy = saving || deleting;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2200, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)',
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
        maxHeight: '85vh', overflowY: 'auto', fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>Edit stringing — {racket.brand} {racket.model}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
          {stringing.archived_at ? 'A past stringing, superseded by a later one.' : 'The current stringing on this racket.'}
        </div>
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', color: '#fca5a5', fontSize: 12.5, marginBottom: 14 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={lbl}>String brand</span>
              <input value={stringBrand} onChange={e => setStringBrand(e.target.value)} placeholder="e.g. Technifibre" style={inp} autoFocus />
            </div>
            <div style={{ flex: 1 }}>
              <span style={lbl}>String model</span>
              <input value={stringModel} onChange={e => setStringModel(e.target.value)} placeholder="e.g. Black Code" style={inp} />
            </div>
          </div>
          <div>
            <span style={lbl}>Gauge (mm)</span>
            <input type="number" step="0.01" value={gauge} onChange={e => setGauge(e.target.value)} placeholder="e.g. 1.28" style={inp} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={lbl}>Main tension (kg)</span>
              <input type="number" step="0.5" value={mainTension} onChange={e => setMainTension(e.target.value)} placeholder="e.g. 21" style={inp} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={lbl}>Cross tension (kg)</span>
              <input type="number" step="0.5" value={crossTension} onChange={e => setCrossTension(e.target.value)} placeholder="e.g. 20" style={inp} />
            </div>
          </div>
          <div>
            <span style={lbl}>Strung on</span>
            <input type="date" value={strungDate} onChange={e => setStrungDate(e.target.value)} style={inp} />
          </div>
          <div>
            <span style={lbl}>Notes</span>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="How did it perform?" style={{ ...inp, resize: 'vertical', fontFamily: C.font }} />
          </div>
          <button onClick={handleSave} disabled={busy} style={{
            background: C.accent, border: 'none', borderRadius: 8, padding: '11px 0',
            color: '#04150f', fontWeight: 700, fontSize: 13.5, cursor: busy ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1, fontFamily: C.font,
          }}>{saving ? 'Saving…' : 'Save changes'}</button>
          <button onClick={handleDelete} disabled={busy} style={{
            background: 'none', border: '1px solid rgba(239,68,68,0.45)', borderRadius: 8, padding: '9px 0',
            color: '#fca5a5', fontWeight: 600, fontSize: 12.5, cursor: busy ? 'default' : 'pointer',
            opacity: deleting ? 0.7 : 1, fontFamily: C.font,
          }}>{deleting ? 'Deleting…' : '🗑 Delete this stringing entry'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Full equipment manager (rackets + shoes, defaults, restringing) ────
function EquipmentManagerModal({ userId, activeSport, onRefresh, onClose }) {
  const [items, setItems] = useState([]);
  const [stringings, setStringings] = useState([]); // current (non-archived) only
  const [defaultsBySport, setDefaultsBySport] = useState({});
  const defaults = defaultsBySport[activeSport] || {};
  const [loading, setLoading] = useState(true);
  // Clicking an item row (vs. the generic "Manage →" button) should land
  // straight on that item's edit form once it loads — see
  // docs_zenanalytics/14-NAVIGATION-SHORTCUTS-2026-10-01.md.
  const [editingItem, setEditingItem] = useState(null); // null | 'new-racket' | 'new-shoes' | item
  const [restringing, setRestringing] = useState(null); // null | racket
  const [busyId, setBusyId] = useState(null);

  useEscapeToClose(onClose);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    // activeSport can be falsy (e.g. a profile that never set primary_sport —
    // found on the real account 2026-10-24: equipment silently vanished
    // because this filter was unconditional, unlike every other sport-scoped
    // query in the codebase). Falling back to unfiltered matches that
    // convention instead of hiding gear behind an unset profile field.
    let itemsQuery = supabase.from('equipment_items').select('*').eq('user_id', userId).order('created_at', { ascending: true });
    if (activeSport) itemsQuery = itemsQuery.eq('sport', activeSport);
    const [{ data: itemsData }, { data: strData }, { data: profile }] = await Promise.all([
      itemsQuery,
      supabase.from('equipment_stringings').select('*').eq('user_id', userId).is('archived_at', null),
      supabase.from('user_profiles').select('default_equipment').eq('user_id', userId).maybeSingle(),
    ]);
    setItems(itemsData || []);
    setStringings(strData || []);
    setDefaultsBySport(profile?.default_equipment || {});
    setLoading(false);
  }, [userId, activeSport]);

  useEffect(() => { load(); }, [load]);

  const refreshAll = async () => { await load(); if (onRefresh) onRefresh(); };

  const setDefault = async (type, id) => {
    setBusyId(id);
    const field = type === 'racket' ? 'racket_id' : 'shoes_id';
    // default_equipment is stored/updated whole, not merged server-side —
    // so every write here has to carry the full map, not just this sport's
    // delta, or it would silently wipe out the other sports' defaults.
    const merged = {
      ...defaultsBySport,
      [activeSport]: { ...defaultsBySport[activeSport], [field]: id },
    };
    const { data: { session } } = await supabase.auth.getSession();
    await fetch(`${API}/profiles/player-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
      },
      body: JSON.stringify({ user_id: userId, default_equipment: merged }),
    }).catch(e => console.error('[setDefault] save failed:', e));
    await refreshAll();
    setBusyId(null);
  };

  const toggleActive = async (item) => {
    setBusyId(item.id);
    await supabase.from('equipment_items').update({ active: !item.active }).eq('id', item.id);
    await refreshAll();
    setBusyId(null);
  };

  const rackets = items.filter(i => i.type === 'racket');
  const shoes = items.filter(i => i.type === 'shoes');
  const stringingFor = (racketId) => stringings.find(s => s.racket_id === racketId) || null;
  const hasStrings = sportHasStrings(activeSport);
  const word = racketLabel(activeSport);

  const ItemRow = ({ item, type }) => {
    const busy = busyId === item.id;
    const isDefault = type === 'racket' ? defaults.racket_id === item.id : defaults.shoes_id === item.id;
    const currentStringing = type === 'racket' && hasStrings ? stringingFor(item.id) : null;
    const stringingText = fmtStringing(currentStringing);
    return (
      <div style={{
        background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${isDefault ? C.accent : 'rgba(249,115,22,0.75)'}`, borderRadius: 10,
        padding: '10px 12px', marginBottom: 8, opacity: busy ? 0.5 : (item.active ? 1 : 0.5), transition: 'opacity 0.15s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: C.text, fontSize: 13.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
              {type === 'racket' ? '🎾' : '👟'} {item.brand} {item.model}
              {isDefault && <span style={{ color: C.accent, fontSize: 10.5, fontWeight: 700, border: `1px solid ${C.accent}`, borderRadius: 20, padding: '1px 7px' }}>DEFAULT</span>}
              {!item.active && <span style={{ color: C.dim, fontSize: 10.5, fontStyle: 'italic' }}>inactive</span>}
            </div>
            {type === 'racket' && hasStrings && (
              <div style={{ color: C.muted, fontSize: 11.5, marginTop: 3 }}>
                {stringingText ? `Strung: ${stringingText}` : 'No stringing logged yet'}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {!isDefault && (
              <button onClick={() => setDefault(type, item.id)} disabled={busy} style={{
                background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted,
                fontSize: 11.5, padding: '5px 9px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
              }}>Set default</button>
            )}
            {type === 'racket' && hasStrings && (
              <button onClick={() => setRestringing(item)} disabled={busy} style={{
                background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted,
                fontSize: 11.5, padding: '5px 9px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
              }}>Restring</button>
            )}
            <button onClick={() => setEditingItem(item)} disabled={busy} style={{
              background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted,
              fontSize: 11.5, padding: '5px 9px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
            }}>Edit</button>
            <button onClick={() => toggleActive(item)} disabled={busy} style={{
              background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted,
              fontSize: 11.5, padding: '5px 9px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
            }}>{item.active ? 'Retire' : 'Reactivate'}</button>
          </div>
        </div>
      </div>
    );
  };

  if (editingItem) {
    return (
      <ItemFormModal
        item={editingItem === 'new-racket' || editingItem === 'new-shoes' ? null : editingItem}
        type={editingItem === 'new-racket' ? 'racket' : editingItem === 'new-shoes' ? 'shoes' : editingItem.type}
        sport={activeSport}
        userId={userId}
        onClose={() => setEditingItem(null)}
        onSaved={() => { setEditingItem(null); refreshAll(); }}
      />
    );
  }

  if (restringing) {
    return (
      <StringingFormModal
        racket={restringing}
        current={stringingFor(restringing.id)}
        userId={userId}
        onClose={() => setRestringing(null)}
        onSaved={() => { setRestringing(null); refreshAll(); }}
      />
    );
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2100, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)',
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 500,
        maxHeight: '85vh', overflowY: 'auto', fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Equipment</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 18, lineHeight: 1.5 }}>
          {hasStrings
            ? `Your default ${word.toLowerCase()}, string setup and shoes are snapshotted onto every new Game Plan automatically. Restringing archives the old setup — past matches keep the gear they were actually played with.`
            : `Your default ${word.toLowerCase()} and shoes are snapshotted onto every new Game Plan automatically.`}
        </div>

        {loading ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{word}s</span>
              <button onClick={() => setEditingItem('new-racket')} style={{
                background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.accent,
                fontSize: 11.5, padding: '4px 9px', cursor: 'pointer', fontFamily: C.font,
              }}>+ Add {word.toLowerCase()}</button>
            </div>
            {rackets.length === 0 ? (
              <div style={{ color: C.dim, fontSize: 13, fontStyle: 'italic', marginBottom: 16 }}>No {word.toLowerCase()}s yet.</div>
            ) : rackets.map(r => <ItemRow key={r.id} item={r} type="racket" />)}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '18px 0 8px' }}>
              <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Shoes</span>
              <button onClick={() => setEditingItem('new-shoes')} style={{
                background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.accent,
                fontSize: 11.5, padding: '4px 9px', cursor: 'pointer', fontFamily: C.font,
              }}>+ Add shoes</button>
            </div>
            {shoes.length === 0 ? (
              <div style={{ color: C.dim, fontSize: 13, fontStyle: 'italic' }}>No shoes yet.</div>
            ) : shoes.map(s => <ItemRow key={s.id} item={s} type="shoes" />)}
          </>
        )}
      </div>
    </div>
  );
}

// ── Compact "Equipment" card for the Game Plan page ──────────────────────
// ── Equipment stat module — table view, same shape as the Competitions
// module (status dropdown default Active, Manage button, a row per item
// with its live stats) instead of the old bare name pills. See
// docs_zenanalytics/11-MODULE-CENTRIC-REDESIGN-2026-09-29.md.
export default function EquipmentStatModule({ userId, activeSport, readOnly = false, demoMode = false, plans = [] }) {
  const [items, setItems] = useState([]);
  const [stringings, setStringings] = useState([]);
  const [showManager, setShowManager] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'all' | 'archived'
  const [historyFor, setHistoryFor] = useState(null); // null | racket item
  // Without this, items/stringings start as [] and the empty-state message
  // ("Add your racket, strings and shoes…") renders immediately, then gets
  // replaced by the real table a moment later once the fetch resolves — a
  // visible flash/layout-jump on every visit that reads as "broken, then
  // fixes itself". Found 2026-10-25 from user report on Create & Manage.
  const [loading, setLoading] = useState(true);
  const hasStrings = sportHasStrings(activeSport);
  const word = racketLabel(activeSport);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Demo mode has no authenticated Supabase session, so RLS
      // (auth.uid() = user_id) blocks a direct query outright — same gap
      // as the rest of Game Plan's demo data, same fix: a public endpoint.
      if (demoMode) {
        try {
          const res = await fetch(`${API}/game-plans/demo/equipment`);
          const data = res.ok ? await res.json() : {};
          setItems(data.items || []);
          setStringings(data.stringings || []);
        } catch {}
        return;
      }
      if (!userId) return;
      let itemsQuery = supabase.from('equipment_items').select('*').eq('user_id', userId);
      if (activeSport) itemsQuery = itemsQuery.eq('sport', activeSport);
      // Fetch every stringing, not just the active one — a racket whose
      // current stringing has since been archived (retired) with nothing
      // new logged yet would otherwise show no period at all here, reading
      // as "no history" even though it has one. See `rowFor` below, which
      // picks the single most relevant (active, else most recent) one per
      // racket to show inline.
      const [{ data: itemsData }, { data: strData }] = await Promise.all([
        itemsQuery,
        supabase.from('equipment_stringings').select('*').eq('user_id', userId),
      ]);
      setItems(itemsData || []);
      setStringings(strData || []);
    } finally {
      setLoading(false);
    }
  }, [userId, demoMode, activeSport]);

  useEffect(() => { load(); }, [load]);

  // Demo data isn't sport-scoped server-side (one shared demo account),
  // so filter client-side there too.
  const sportItems = demoMode && activeSport ? items.filter(i => i.sport === activeSport) : items;
  const filtered = sportItems.filter(i =>
    statusFilter === 'all' ? true : statusFilter === 'active' ? i.active !== false : i.active === false
  );

  const rowFor = (item) => {
    if (item.type === 'racket') {
      // The active stringing if there is one, else the most recently
      // strung one — so a racket between stringings (current one retired,
      // nothing new logged yet) still shows its last known period instead
      // of nothing. See `load` above: `stringings` now holds the racket's
      // full history, not just the active row.
      const racketStringings = hasStrings ? stringings.filter(s => s.racket_id === item.id) : [];
      const stringing = racketStringings.find(s => !s.archived_at)
        || [...racketStringings].sort((a, b) => (b.strung_date || '').localeCompare(a.strung_date || ''))[0]
        || null;
      const u = stringing
        ? usageFor(plans, null, null, 'equipment_stringing_id', stringing.id)
        : usageFor(plans, 'equipment_racket_id', item.id);
      const strungLabel = fmtStringing(stringing);
      const period = stringing?.strung_date
        ? (stringing.archived_at
            ? `${formatDateLocaleAware(stringing.strung_date)} – ${formatDateLocaleAware(stringing.archived_at.slice(0, 10))}`
            : `since ${formatDateLocaleAware(stringing.strung_date)} (ongoing)`)
        : null;
      let sub = period ? `${strungLabel ? strungLabel + ' · ' : ''}${period}` : strungLabel;
      // Matches/Win%/Avg Perf here are scoped to the CURRENT stringing, not
      // the racket's lifetime -- a fresh restring genuinely has zero matches
      // yet, which otherwise reads as "broken" rather than "new". Feedback
      // 2026-11-02.
      if (stringing && !u.matchCount) sub = `${sub ? sub + ' · ' : ''}no matches with this stringing yet`;
      return { item, u, sub };
    }
    return { item, u: usageFor(plans, 'equipment_shoes_id', item.id), sub: null };
  };

  const selStyle = {
    background: 'rgba(255,255,255,0.09)', border: `1px solid ${C.border}`, borderRadius: 6,
    color: C.text, fontSize: 11.5, padding: '4px 8px', fontFamily: C.font, cursor: 'pointer',
  };

  return (
    <>
    <div style={{
      background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
      border: `1.5px solid ${C.moduleBorder}`, borderRadius: 12, padding: '10px 14px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.blue, fontSize: 14 }}>🎾</span>
          <span style={{ color: C.logoBlue, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Equipment
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selStyle}>
            <option value="active">Active</option>
            <option value="all">All</option>
            <option value="archived">Archived</option>
          </select>
          <button onClick={() => setShowManager(true)} disabled={readOnly} style={{
            background: readOnly ? 'none' : 'rgba(16,185,129,0.12)', border: `1px solid ${readOnly ? C.border : C.accent}`,
            borderRadius: 6, color: readOnly ? C.dim : C.accent, fontSize: 11.5, fontWeight: 700, padding: '5px 12px',
            cursor: readOnly ? 'default' : 'pointer', fontFamily: C.font,
          }}>Manage →</button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: C.dim, fontSize: 13, marginTop: 8 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div onClick={() => !readOnly && setShowManager(true)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => !readOnly && setShowManager(true))} style={{
          color: C.dim, fontSize: 13, marginTop: 8, fontStyle: 'italic', cursor: readOnly ? 'default' : 'pointer',
        }}>
          {sportItems.length === 0
            ? `Add your ${word.toLowerCase()}${hasStrings ? ', strings' : ''} and shoes to track performance by setup…`
            : `No ${statusFilter} items.`}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid rgba(56,189,248,0.45)` }}>
                {['Item', 'Matches', 'Win%', 'Avg Perf', ''].map((h, i) => (
                  <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '4px 6px', color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => {
                const { u, sub } = rowFor(item);
                return (
                  <tr key={item.id} onClick={() => !readOnly && setShowManager(true)} style={{
                    borderBottom: `1px solid rgba(255,255,255,0.05)`, cursor: readOnly ? 'default' : 'pointer',
                  }}
                    onMouseEnter={e => { if (!readOnly) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '7px 6px', color: C.text, fontWeight: 600 }}>
                      {item.type === 'racket' ? '🎾' : '👟'} {item.brand} {item.model}
                      {item.active === false && <span style={{ color: C.dim, fontWeight: 400 }}> · retired</span>}
                      {sub && <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 400 }}>{sub}</div>}
                    </td>
                    <td style={{ padding: '7px 6px', textAlign: 'right', color: C.text }}>{u.matchCount || '—'}</td>
                    <td style={{ padding: '7px 6px', textAlign: 'right', color: C.text }}>{u.winRate != null ? `${u.winRate}%` : '—'}</td>
                    <td style={{ padding: '7px 6px', textAlign: 'right', color: C.accent }}>{u.avgPerf ? `${u.avgPerf}★` : '—'}</td>
                    <td style={{ padding: '7px 6px', textAlign: 'right' }}>
                      {item.type === 'racket' && hasStrings && (
                        <button
                          onClick={e => { e.stopPropagation(); setHistoryFor(item); }}
                          title="View this racket's full stringing history"
                          style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '2px 4px', whiteSpace: 'nowrap', fontFamily: C.font }}
                        >String history</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
    {/* Sibling of the card, not nested inside it — the card's own
        backdropFilter creates a stacking context that traps a
        position:fixed modal behind whatever renders after it in the DOM.
        See docs_zenanalytics note 2026-10-02 (HomeView.jsx fix). */}
    {showManager && (
      <EquipmentManagerModal
        userId={userId} activeSport={activeSport} onRefresh={load} onClose={() => setShowManager(false)}
      />
    )}
    {historyFor && (
      <StringingHistoryModal
        racket={historyFor} userId={userId} demoMode={demoMode} plans={plans}
        onClose={() => setHistoryFor(null)}
      />
    )}
    </>
  );
}

// ── Stringing history modal — every (re)stringing a racket has had, newest
// first, active one flagged and always on top. Shows the 10 most recent by
// default with a "Show full history" toggle, since a racket strung often
// over years could otherwise produce a very long list. See user request
// 2026-10-24: a racket's stringing history deserves the same per-setup
// record (matches/hours/win%) that switching rackets or shoes already gets.
function StringingHistoryModal({ racket, userId, demoMode, plans, onClose }) {
  useEscapeToClose(onClose);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (demoMode) {
        try {
          const res = await fetch(`${API}/game-plans/demo/equipment`);
          const data = res.ok ? await res.json() : {};
          if (!cancelled) setAll((data.stringings || []).filter(s => s.racket_id === racket.id));
        } catch { if (!cancelled) setAll([]); }
      } else if (userId) {
        const { data } = await supabase.from('equipment_stringings').select('*')
          .eq('user_id', userId).eq('racket_id', racket.id);
        if (!cancelled) setAll(data || []);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [racket.id, userId, demoMode]);

  // Active (archived_at IS NULL) always first, then newest-strung first.
  const sorted = [...all].sort((a, b) => {
    if (!a.archived_at && b.archived_at) return -1;
    if (a.archived_at && !b.archived_at) return 1;
    return (b.strung_date || '').localeCompare(a.strung_date || '');
  });
  const visible = showAll ? sorted : sorted.slice(0, 10);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.card, border: `1px solid ${C.moduleBorder}`, borderRadius: 14,
        padding: '20px 22px', width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto',
        fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ color: C.text, fontWeight: 800, fontSize: 15 }}>
            📜 {racket.brand} {racket.model} — stringing history
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {loading ? (
          <div style={{ color: C.muted, fontSize: 13, marginTop: 16 }}>Loading…</div>
        ) : sorted.length === 0 ? (
          <div style={{ color: C.dim, fontSize: 13, marginTop: 16, fontStyle: 'italic' }}>No stringings logged for this racket yet.</div>
        ) : (
          <>
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visible.map(s => {
                const u = usageFor(plans, null, null, 'equipment_stringing_id', s.id);
                const isCurrent = !s.archived_at;
                return (
                  <div key={s.id} style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${isCurrent ? 'rgba(16,185,129,0.4)' : C.border}`,
                    borderRadius: 10, padding: '10px 12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ color: C.text, fontSize: 12.5, fontWeight: 700 }}>
                        {fmtStringing(s) || 'Stringing'}
                        {isCurrent && <span style={{ color: C.accent, fontWeight: 700, fontSize: 11 }}> · Active</span>}
                      </div>
                      <div style={{ color: C.muted, fontSize: 11 }}>
                        {s.strung_date ? `strung ${formatDateLocaleAware(s.strung_date)}` : ''}{s.archived_at ? ` → ${formatDateLocaleAware(s.archived_at.slice(0, 10))}` : ''}
                      </div>
                    </div>
                    <div style={{ marginTop: 4 }}><UsageStrip u={u} /></div>
                    {s.notes && (
                      <div style={{ color: C.muted, fontSize: 11.5, marginTop: 5, fontStyle: 'italic', lineHeight: 1.4 }}>
                        "{s.notes}"
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {sorted.length > 10 && !showAll && (
              <button onClick={() => setShowAll(true)} style={{
                marginTop: 12, width: '100%', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`,
                borderRadius: 8, color: C.text, fontSize: 12.5, fontWeight: 600, padding: '8px', cursor: 'pointer', fontFamily: C.font,
              }}>Show full history ({sorted.length} total) →</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── "Stats by Equipment" — Game Stats tab ─────────────────────────────────
// Per-item permanent history (backlog F): every racket and every shoe gets
// its own card; a racket's card breaks down further into its stringing
// history, because that's what actually changes how the racket plays. A
// restring archives the old stringing row rather than deleting it, so the
// full history is always here — nothing to migrate.

function daysBetween(fromISO, toISO) {
  if (!fromISO) return null;
  const from = new Date(fromISO.slice(0, 10) + 'T12:00:00');
  const to = toISO ? new Date(toISO.slice(0, 10) + 'T12:00:00') : new Date();
  return Math.max(0, Math.round((to - from) / 86400000));
}

// Usage summary for one piece of gear — matches it was snapshotted on
// (equipment_*_id) plus non-match sessions logged with it, counted and
// scored separately since "how did I do with it" only applies to matches.
function usageFor(plans, matchField, matchId, stringField, stringId) {
  const sessions = plans.filter(p =>
    stringId ? p[stringField] === stringId : p[matchField] === matchId
  );
  const matches = sessions.filter(p => ['match', 'competition', 'race_day'].includes(p.type) && p.result);
  const trainings = sessions.filter(p => !['match', 'competition', 'race_day'].includes(p.type));
  const w = matches.filter(p => p.result === 'win').length;
  const d = matches.filter(p => p.result === 'draw').length;
  const l = matches.filter(p => p.result === 'loss').length;
  // Hours only from an actually-logged duration — never estimated (see
  // backlog F decision: no hours shown at all if none were logged).
  const minutesLogged = sessions.filter(p => p.actual_duration_minutes).map(p => p.actual_duration_minutes);
  const hours = minutesLogged.length ? (minutesLogged.reduce((a, b) => a + b, 0) / 60).toFixed(1) : null;
  const avgOf = (field) => {
    const vals = matches.filter(p => p[field]).map(p => p[field]);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };
  return {
    matchCount: matches.length, trainingCount: trainings.length, hours,
    w, d, l, winRate: matches.length ? Math.round((w / matches.length) * 100) : null,
    avgPerf: avgOf('performance_score'),
    serve: avgOf('serve_rating'), ret: avgOf('return_rating'),
    forehand: avgOf('forehand_rating'), backhand: avgOf('backhand_rating'), netPlay: avgOf('net_play_rating'),
  };
}

function UsageStrip({ u }) {
  const parts = [];
  parts.push(`${u.matchCount} match${u.matchCount !== 1 ? 'es' : ''}`);
  if (u.trainingCount) parts.push(`${u.trainingCount} training${u.trainingCount !== 1 ? 's' : ''}`);
  if (u.hours) parts.push(`${u.hours}h logged`);
  return (
    <div style={{ color: C.muted, fontSize: 11.5 }}>
      {parts.join(' · ')}
      {u.matchCount > 0 && (
        <span> · <span style={{ color: C.text, fontWeight: 700 }}>{u.w}W-{u.d}D-{u.l}L</span> ({u.winRate}%)</span>
      )}
    </div>
  );
}

function RatingChips({ u }) {
  // Icon-free by design (user feedback, 2026-10-30): the generic rating
  // emoji (e.g. 🥅 for "Net play") reads as a hockey goal/unrelated icon
  // next to gear stats, not as a racket-sport signifier — plain text labels
  // avoid that mismatch entirely rather than hunting for a better emoji.
  const rows = [
    ['Serve', u.serve], ['Return', u.ret], ['Forehand', u.forehand],
    ['Backhand', u.backhand], ['Net play', u.netPlay],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
      {rows.map(([label, v]) => (
        <span key={label} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 20, padding: '2px 9px', fontSize: 11, color: C.muted }}>
          {label} {v}★
        </span>
      ))}
    </div>
  );
}

function RacketCard({ racket, stringingsForRacket, plans, onEditStringing }) {
  const overall = usageFor(plans, 'equipment_racket_id', racket.id);
  const hasStrings = sportHasStrings(racket.sport);
  const sorted = hasStrings
    ? [...stringingsForRacket].sort((a, b) => (b.strung_date || '').localeCompare(a.strung_date || ''))
    : [];
  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ color: C.logoBlue, fontSize: 15, fontWeight: 800 }}>
          🎾 {racket.brand} {racket.model}
          {racket.active === false && <span style={{ color: C.dim, fontWeight: 500, fontSize: 11.5 }}> · retired</span>}
        </div>
      </div>
      <UsageStrip u={overall} />
      <RatingChips u={overall} />

      {sorted.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.moduleBorder}`, paddingTop: 10 }}>
          <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Stringing history
          </div>
          {sorted.map(s => {
            const u = usageFor(plans, null, null, 'equipment_stringing_id', s.id);
            const days = daysBetween(s.strung_date, s.archived_at);
            const isCurrent = !s.archived_at;
            return (
              <div key={s.id} style={{
                background: 'rgba(255,255,255,0.03)', border: `1px solid ${isCurrent ? 'rgba(16,185,129,0.35)' : 'rgba(56,189,248,0.35)'}`,
                borderRadius: 10, padding: '10px 12px', marginBottom: 6,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ color: C.text, fontSize: 12.5, fontWeight: 700 }}>
                    {fmtStringing(s) || 'Stringing'}
                    {isCurrent && <span style={{ color: C.accent, fontWeight: 700, fontSize: 11 }}> · current</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ color: C.muted, fontSize: 11 }}>
                      {/* Was rendering the raw ISO string (strung_date) directly
                          instead of running it through the app's own locale-aware
                          formatter, so a Finnish (or any non-US) athlete saw
                          "2026-10-24" instead of their own date convention.
                          Feedback 2026-11-02. */}
                      {s.strung_date ? `since ${formatDateLocaleAware(s.strung_date, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}{days != null ? ` · ${days} day${days !== 1 ? 's' : ''}` : ''}
                    </div>
                    {/* A bare "Delete" button here was too blunt for fixing a
                        typo'd gauge/tension/date -- Edit opens the same form
                        Restring uses, pre-filled, with delete available from
                        inside it too. Feedback 2026-11-02. */}
                    {onEditStringing && (
                      <button type="button" onClick={() => onEditStringing(s)} title="Edit this stringing entry" style={{
                        background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted,
                        fontSize: 10.5, padding: '3px 7px', cursor: 'pointer', fontFamily: C.font, flexShrink: 0,
                      }}>✎ Edit</button>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 4 }}><UsageStrip u={u} /></div>
                <RatingChips u={u} />
                {s.notes && (
                  <div style={{ color: C.muted, fontSize: 11.5, marginTop: 5, fontStyle: 'italic', lineHeight: 1.4 }}>
                    "{s.notes}"
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ShoesCard({ shoes, plans }) {
  const u = usageFor(plans, 'equipment_shoes_id', shoes.id);
  // equipment_items has no "retired at" timestamp, only created_at — so a
  // retired pair's "in use" span still runs to today rather than closing
  // at retirement. Close enough until the schema grows a retired_at column.
  const days = daysBetween(shoes.created_at, null);
  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${C.moduleBorder}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ color: C.logoBlue, fontSize: 15, fontWeight: 800 }}>
          👟 {shoes.brand} {shoes.model}
          {shoes.active === false && <span style={{ color: C.dim, fontWeight: 500, fontSize: 11.5 }}> · retired</span>}
        </div>
        {days != null && <div style={{ color: C.muted, fontSize: 11 }}>{days} day{days !== 1 ? 's' : ''} in use</div>}
      </div>
      <UsageStrip u={u} />
      <RatingChips u={u} />
    </div>
  );
}

export function EquipmentStatsView({ plans, userId, activeSport, demoMode = false }) {
  const [items, setItems] = useState([]);
  const [stringings, setStringings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { stringing, racket } | null

  useEffect(() => {
    // Demo mode has no authenticated session — RLS blocks a direct query,
    // same gap as EquipmentSection above. Use the public demo endpoint.
    if (demoMode) {
      fetch(`${API}/game-plans/demo/equipment`)
        .then(r => r.ok ? r.json() : {})
        .then(data => {
          setItems(data.items || []);
          setStringings(data.stringings || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
      return;
    }
    if (!userId) { setLoading(false); return; }
    Promise.all([
      supabase.from('equipment_items').select('*').eq('user_id', userId).eq('sport', activeSport),
      supabase.from('equipment_stringings').select('*').eq('user_id', userId), // all, including archived
    ]).then(([{ data: itemsData }, { data: strData }]) => {
      setItems(itemsData || []);
      setStringings(strData || []);
      setLoading(false);
    });
  }, [userId, demoMode, activeSport]);

  // Demo data isn't sport-scoped server-side (one shared demo account).
  const sportFilteredItems = demoMode && activeSport ? items.filter(i => i.sport === activeSport) : items;
  const rackets = useMemo(() => sportFilteredItems.filter(i => i.type === 'racket'), [sportFilteredItems]);
  const shoes = useMemo(() => sportFilteredItems.filter(i => i.type === 'shoes'), [sportFilteredItems]);
  const stringingsByRacket = useMemo(() => {
    const map = {};
    stringings.forEach(s => { (map[s.racket_id] ||= []).push(s); });
    return map;
  }, [stringings]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted }}>Loading…</div>;
  }

  if (sportFilteredItems.length === 0) {
    const word = racketLabel(activeSport);
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>🎾</div>
        <div style={{ fontSize: 14 }}>No equipment added yet.</div>
        <div style={{ fontSize: 12.5, marginTop: 4 }}>
          Add your {word.toLowerCase()}{sportHasStrings(activeSport) ? ', strings' : ''} and shoes in the Game Plan tab — new matches will track it from there.
        </div>
      </div>
    );
  }

  return (
    <div>
      {rackets.map(r => (
        <RacketCard key={r.id} racket={r} stringingsForRacket={stringingsByRacket[r.id] || []} plans={plans}
          onEditStringing={demoMode ? null : (s) => setEditing({ stringing: s, racket: r })} />
      ))}
      {shoes.map(s => (
        <ShoesCard key={s.id} shoes={s} plans={plans} />
      ))}
      {editing && (
        <EditStringingModal
          stringing={editing.stringing}
          racket={editing.racket}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            supabase.from('equipment_stringings').select('*').eq('user_id', userId).then(({ data }) => setStringings(data || []));
          }}
          onDeleted={() => {
            setStringings(cur => cur.filter(x => x.id !== editing.stringing.id));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

export { fmtStringing };
